// src/components/suppliers/forms/UpdateSupplierForm.tsx
import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";

import {
  FiUser,
  FiGlobe,
  FiMapPin,
  FiTag,
  FiHash,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

import {
  type SupplierFormData,
  type ReservationContact,
  type AccountContact,
  type CallCentreContact,
} from "@/pages/suppliers/useSuppliersPage";

import { useProductsList } from "@/api/features/products/queries";

type FormState = SupplierFormData & {
  /** Local-only multi-select of products (by id) */
  productIds: number[];
  /** Local-only flag to open/close products dropdown */
  productsOpen: boolean;
};

export default function UpdateSupplierForm({
  value,
  onSubmit,
  onSendPassword,
  onCancel,
  submitting = false,
  errorMessage,
}: {
  value: SupplierFormData;
  onSubmit: (payload: SupplierFormData) => void;
  onSendPassword: (email: string) => void;
  onCancel?: () => void;
  submitting?: boolean;
  errorMessage?: string | null;
}) {
  const [form, setForm] = useState<FormState>({
    ...value,
    productIds: (value as any).productIds ?? [],
    productsOpen: false,
  });

  const [localError, setLocalError] = useState<string | null>(null);
  const [showPortalPassword, setShowPortalPassword] = useState(false);

  // 🔹 Sync internal form state whenever the parent `value` changes
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      ...value,
      company: {
        ...prev.company,
        ...value.company,
      },
      owner: {
        ...prev.owner,
        ...value.owner,
      },
      login: {
        ...prev.login,
        ...value.login,
      },
      reservations: value.reservations ?? prev.reservations,
      accounts: value.accounts ?? prev.accounts,
      callCentres: value.callCentres ?? prev.callCentres,
      productIds: (value as any).productIds ?? prev.productIds ?? [],
      productsOpen: prev.productsOpen,
      active: value.active,
    }));
  }, [value]);

  // 🔹 Dynamic products list (page 1, up to 100 items)
  const {
    data: productsPage,
    isLoading: productsLoading,
    isError: productsError,
  } = useProductsList({ page: 1, limit: 100 });

  const productOptions = productsPage?.items ?? [];

  const change = <K extends keyof SupplierFormData>(
    key: K,
    value: SupplierFormData[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (localError) setLocalError(null);
  };

  const toDigits = (v?: string) => (v || "").replace(/\D/g, "");

  // 🔹 Toggle product in productIds[]
  const toggleProduct = (id: number) => {
    setForm((prev) => {
      const exists = prev.productIds.includes(id);
      const productIds = exists
        ? prev.productIds.filter((pid) => pid !== id)
        : [...prev.productIds, id];

      if (localError) setLocalError(null);

      return {
        ...prev,
        productIds,
      };
    });
  };

  // ----- Reservation helpers -----
  const setReservation = (i: number, k: keyof ReservationContact, v: string) => {
    const copy = form.reservations.slice();
    copy[i] = { ...copy[i], [k]: v };
    change("reservations", copy);
  };
  const addReservation = () =>
    change("reservations", [
      ...form.reservations,
      { name: "", mobile: "", email: "" },
    ]);
  const removeReservation = (i: number) =>
    change("reservations", form.reservations.filter((_, idx) => idx !== i));

  // ----- Accounts helpers -----
  const setAccount = (i: number, k: keyof AccountContact, v: string) => {
    const copy = form.accounts.slice();
    copy[i] = { ...copy[i], [k]: v };
    change("accounts", copy);
  };
  const addAccount = () =>
    change("accounts", [...form.accounts, { name: "", mobile: "", email: "" }]);
  const removeAccount = (i: number) =>
    change("accounts", form.accounts.filter((_, idx) => idx !== i));

  // ----- Call centre helpers -----
  const setCallCentre = (i: number, k: keyof CallCentreContact, v: string) => {
    const copy = form.callCentres.slice();
    copy[i] = { ...copy[i], [k]: v };
    change("callCentres", copy);
  };
  const addCallCentre = () =>
    change("callCentres", [
      ...form.callCentres,
      { callCentreNumber: "", emergencyNumber: "" },
    ]);
  const removeCallCentre = (i: number) =>
    change("callCentres", form.callCentres.filter((_, idx) => idx !== i));

  const handleCancel = () => {
    if (onCancel) onCancel();
    else history.back();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.supplierCompanyName.trim()) {
      setLocalError("Please enter supplier company name.");
      return;
    }
    if (!form.managerFirstName.trim() || !form.managerLastName.trim()) {
      setLocalError("Please enter manager first and last name.");
      return;
    }
    if (!form.managerEmail.trim()) {
      setLocalError("Please enter manager email.");
      return;
    }
    if (!form.managerMobile.trim()) {
      setLocalError("Please enter manager mobile number.");
      return;
    }
    if (!form.company.country || !form.company.state || !form.company.city) {
      setLocalError("Please select country, state, and city.");
      return;
    }
    // 🔹 Deep update: require at least one product
    if (!form.productIds || form.productIds.length === 0) {
      setLocalError("Please select at least one product.");
      return;
    }
    // 🔹 Require Owner Email (backend uses this for credentials)
    const ownerEmail = (form.owner.email || "").trim();
    if (!ownerEmail) {
      setLocalError("Please enter Owner Email (credentials will be emailed here).");
      return;
    }

    setLocalError(null);

    // productIds/productsOpen are local-only;
    // we pass productIds along so mapFormToUpdateBody can use it.
    onSubmit({
      ...form,
      productIds: form.productIds,
    } as SupplierFormData);
  };

  const bannerRaw = localError || errorMessage || null;

  // 🔹 Support multiple messages (we join with " | " on the page)
  const bannerMessages =
    bannerRaw && typeof bannerRaw === "string"
      ? bannerRaw
          .split("|")
          .map((m) => m.trim())
          .filter(Boolean)
      : [];

  const triggerSendPassword = () => {
    const email = (form.owner.email || "").trim();
    if (!email) {
      setLocalError(
        "Please set an Owner Email before sending password (credentials will be emailed to the owner)."
      );
      return;
    }
    onSendPassword(email);
  };

  // ----- Owner name helpers (same UX as Create: first + last) -----
  const parseOwnerName = () => {
    const full = (form.owner.name || "").trim();
    if (!full) return { first: "", last: "" };
    const parts = full.split(" ");
    const first = parts[0] ?? "";
    const last = parts.slice(1).join(" ");
    return { first, last };
  };

  const { first: ownerFirstName, last: ownerLastName } = parseOwnerName();

  const updateOwnerFirstName = (first: string) => {
    const { last } = parseOwnerName();
    const combined = [first.trim(), last.trim()].filter(Boolean).join(" ");
    change("owner", { ...form.owner, name: combined });
  };

  const updateOwnerLastName = (last: string) => {
    const { first } = parseOwnerName();
    const combined = [first.trim(), last.trim()].filter(Boolean).join(" ");
    change("owner", { ...form.owner, name: combined });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Error banner */}
      {bannerMessages.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {bannerMessages.length === 1 ? (
            <span>{bannerMessages[0]}</span>
          ) : (
            <ul className="list-disc pl-4">
              {bannerMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Single card */}
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">

        {/* A. Supplier */}
        <div className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Supplier</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-6">
              <Field label="Supplier Company Name">
                <Input
                  value={form.supplierCompanyName}
                  onChange={(e) => change("supplierCompanyName", e.target.value)}
                  placeholder="Company (public/commercial)"
                  required
                />
              </Field>
            </div>
            <div className="sm:col-span-6">
              <Field label="Products" icon={<FiTag />}>
                <div className="relative">
                  <button
                    type="button"
                    className="flex min-h-[38px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-2 py-1 text-left text-sm hover:border-gray-400"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, productsOpen: !prev.productsOpen }))
                    }
                    disabled={productsLoading || productsError}
                  >
                    <span className="truncate text-gray-700">
                      {productsLoading
                        ? "Loading products…"
                        : productsError
                        ? "Failed to load products"
                        : productOptions.length === 0
                        ? "No products found"
                        : form.productIds.length === 0
                        ? "— Select products —"
                        : productOptions
                            .filter((p: any) => form.productIds.includes(p.id))
                            .map((p: any) => p.name)
                            .join(", ")}
                    </span>
                    <span className="ml-2 text-xs text-gray-400">▼</span>
                  </button>
                  {form.productsOpen && !productsLoading && !productsError && (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow">
                      {productOptions.length === 0 ? (
                        <div className="px-2 py-1 text-xs text-gray-400">
                          No products found. Create products first in the Products module.
                        </div>
                      ) : (
                        productOptions.map((p: any) => {
                          const selected = form.productIds.includes(p.id);
                          return (
                            <div
                              key={p.id ?? p.name}
                              onClick={() => toggleProduct(p.id)}
                              className={`flex cursor-pointer items-center justify-between px-2 py-1 text-sm hover:bg-gray-100 ${selected ? "bg-sky-50 text-sky-600" : ""}`}
                            >
                              <span className="truncate">{p.name}</span>
                              {selected && <span className="text-[11px] font-medium">✔</span>}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {form.productIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {productOptions
                      .filter((p: any) => form.productIds.includes(p.id))
                      .map((p: any) => (
                        <span key={p.id} className="flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700">
                          {p.name}
                        </span>
                      ))}
                  </div>
                )}
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Manager First Name" icon={<FiUser />}>
                <Input
                  value={form.managerFirstName}
                  onChange={(e) => change("managerFirstName", e.target.value)}
                  placeholder="First name"
                  required
                />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Manager Last Name" icon={<FiUser />}>
                <Input
                  value={form.managerLastName}
                  onChange={(e) => change("managerLastName", e.target.value)}
                  placeholder="Last name"
                  required
                />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Manager Email ID" icon={<FiUser />}>
                <Input
                  type="email"
                  value={form.managerEmail}
                  onChange={(e) => change("managerEmail", e.target.value)}
                  placeholder="manager@company.com"
                  required
                />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Manager Mobile Number">
                <PhoneInput
                  country="in"
                  value={toDigits(form.managerMobile)}
                  onChange={(v) => change("managerMobile", v ? `+${v}` : "")}
                  inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                  buttonClass="!border-gray-300"
                  containerClass="!w-full"
                />
              </Field>
            </div>
          </div>
        </div>

        {/* B. Company Details */}
        <div className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Company Details</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-6">
              <Field label="Website URL" icon={<FiGlobe />}>
                <Input
                  value={form.company.website}
                  onChange={(e) => change("company", { ...form.company, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </Field>
            </div>
            <div className="sm:col-span-6">
              <Field label="Pincode / Zip" icon={<FiHash />}>
                <Input
                  value={form.company.pincode}
                  onChange={(e) => change("company", { ...form.company, pincode: e.target.value })}
                  placeholder="Postal code"
                />
              </Field>
            </div>
            <div className="sm:col-span-6">
              <Field label="Address Line 1" icon={<FiMapPin />}>
                <Input
                  value={form.company.addressLine1}
                  onChange={(e) => change("company", { ...form.company, addressLine1: e.target.value })}
                  placeholder="Street / Building"
                />
              </Field>
            </div>
            <div className="sm:col-span-6">
              <Field label="Address Line 2">
                <Input
                  value={form.company.addressLine2}
                  onChange={(e) => change("company", { ...form.company, addressLine2: e.target.value })}
                  placeholder="Area / Landmark (optional)"
                />
              </Field>
            </div>
            <div className="sm:col-span-12">
              <LocationRow
                value={{ country: form.company.country, state: form.company.state, city: form.company.city }}
                onChange={(v: CSCFormValue) => {
                  change("company", { ...form.company, country: v.country || "", state: v.state || "", city: v.city || "" });
                }}
                instancePrefix="supplier-company-csc-edit"
              />
            </div>
            <div className="sm:col-span-6">
              <Field label="Login ID">
                <Input
                  value={form.company.loginId}
                  onChange={(e) => change("company", { ...form.company, loginId: e.target.value })}
                  placeholder="Unique login (for B2B portal, if applicable)"
                />
              </Field>
            </div>
            <div className="sm:col-span-6">
              <Field label="Portal Password">
                <div className="flex items-center gap-2">
                  <Input
                    type={showPortalPassword ? "text" : "password"}
                    value={form.company.password}
                    onChange={(e) => change("company", { ...form.company, password: e.target.value })}
                    placeholder="Leave blank to keep existing"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPortalPassword((v) => !v)}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50"
                    aria-label={showPortalPassword ? "Hide portal password" : "Show portal password"}
                  >
                    {showPortalPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </Field>
            </div>
          </div>
        </div>

        {/* C. Reservation Details */}
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reservation Details</p>
            <button
              type="button"
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={addReservation}
            >
              + Add Reservation
            </button>
          </div>
          <div className="space-y-4">
            {form.reservations.map((r, idx) => (
              <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Field label="Concern Person Name">
                    <Input value={r.name} onChange={(e) => setReservation(idx, "name", e.target.value)} placeholder="Name" />
                  </Field>
                </div>
                <div className="sm:col-span-4">
                  <Field label="Mobile No">
                    <PhoneInput
                      country="in"
                      value={toDigits(form.reservations[idx].mobile)}
                      onChange={(v) => setReservation(idx, "mobile", v ? `+${v}` : "")}
                      inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                      buttonClass="!border-gray-300"
                      containerClass="!w-full"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-4">
                  <Field label="Email ID">
                    <Input type="email" value={r.email} onChange={(e) => setReservation(idx, "email", e.target.value)} placeholder="name@email.com" />
                  </Field>
                </div>
                {form.reservations.length > 1 && (
                  <div className="sm:col-span-12 text-right">
                    <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => removeReservation(idx)}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* D. Accounts */}
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Accounts</p>
            <button
              type="button"
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={addAccount}
            >
              + Add Account
            </button>
          </div>
          <div className="space-y-4">
            {form.accounts.map((a, idx) => (
              <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Field label="Concern Person Name">
                    <Input value={a.name} onChange={(e) => setAccount(idx, "name", e.target.value)} placeholder="Name" />
                  </Field>
                </div>
                <div className="sm:col-span-4">
                  <Field label="Mobile Number">
                    <PhoneInput
                      country="in"
                      value={toDigits(form.accounts[idx].mobile)}
                      onChange={(v) => setAccount(idx, "mobile", v ? `+${v}` : "")}
                      inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                      buttonClass="!border-gray-300"
                      containerClass="!w-full"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-4">
                  <Field label="Email ID">
                    <Input type="email" value={a.email} onChange={(e) => setAccount(idx, "email", e.target.value)} placeholder="name@email.com" />
                  </Field>
                </div>
                {form.accounts.length > 1 && (
                  <div className="sm:col-span-12 text-right">
                    <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => removeAccount(idx)}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* E. Call Centre / Emergency */}
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Call Centre / Emergency (24×7)</p>
            <button
              type="button"
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={addCallCentre}
            >
              + Add Contact
            </button>
          </div>
          <div className="space-y-4">
            {form.callCentres.map((c, idx) => (
              <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-3">
                  <Field label="Call Centre Number">
                    <PhoneInput
                      country="in"
                      value={toDigits(c.callCentreNumber)}
                      onChange={(v) => setCallCentre(idx, "callCentreNumber", v ? `+${v}` : "")}
                      inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                      buttonClass="!border-gray-300"
                      containerClass="!w-full"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label="Call Centre Name">
                    <Input value={c.callCentreName || ""} onChange={(e) => setCallCentre(idx, "callCentreName", e.target.value)} placeholder="Name (optional)" />
                  </Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label="Emergency Number">
                    <PhoneInput
                      country="in"
                      value={toDigits(c.emergencyNumber)}
                      onChange={(v) => setCallCentre(idx, "emergencyNumber", v ? `+${v}` : "")}
                      inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                      buttonClass="!border-gray-300"
                      containerClass="!w-full"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label="Notes">
                    <Input value={c.notes || ""} onChange={(e) => setCallCentre(idx, "notes", e.target.value)} placeholder="Instructions (optional)" />
                  </Field>
                </div>
                {form.callCentres.length > 1 && (
                  <div className="sm:col-span-12 text-right">
                    <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => removeCallCentre(idx)}>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* F. Owner Details */}
        <div className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Owner Details</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-3">
              <Field label="Owner First Name" icon={<FiUser />}>
                <Input value={ownerFirstName} onChange={(e) => updateOwnerFirstName(e.target.value)} placeholder="First name" />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Owner Last Name" icon={<FiUser />}>
                <Input value={ownerLastName} onChange={(e) => updateOwnerLastName(e.target.value)} placeholder="Last name" />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Mobile No">
                <PhoneInput
                  country="in"
                  value={toDigits(form.owner.mobile)}
                  onChange={(v) => change("owner", { ...form.owner, mobile: v ? `+${v}` : "" })}
                  inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
                  buttonClass="!border-gray-300"
                  containerClass="!w-full"
                />
              </Field>
            </div>
            <div className="sm:col-span-3">
              <Field label="Owner Email ID">
                <Input
                  type="email"
                  value={form.owner.email}
                  onChange={(e) => change("owner", { ...form.owner, email: e.target.value })}
                  placeholder="owner@email.com"
                />
              </Field>
            </div>
          </div>
        </div>

        {/* G. Supplier Login */}
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Supplier Login</p>
            <button type="button" className="text-xs font-medium text-sky-700 hover:underline" onClick={triggerSendPassword}>
              Send password email to Owner
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
            {form.login.ownerUsername && (
              <div className="sm:col-span-6">
                <Field label="Username">
                  <Input value={form.login.ownerUsername} readOnly className="cursor-default bg-gray-50 text-gray-600" />
                </Field>
              </div>
            )}
            <div className={form.login.ownerUsername ? "sm:col-span-6" : "sm:col-span-12"}>
              <Field label="Note (optional)">
                <Input
                  value={form.login.note || ""}
                  onChange={(e) => change("login", { ...form.login, note: e.target.value })}
                  placeholder="Short note (internal use)"
                />
              </Field>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Username is backend-managed. Credentials will be emailed to the <strong>Owner Email</strong>.
          </p>
        </div>

        {/* H. Status */}
        <div className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Status</p>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <span className={`text-xs ${form.active ? "text-green-600" : "text-gray-500"}`}>
              {form.active ? "Active" : "Inactive"}
            </span>
            <input type="checkbox" className="sr-only" checked={!!form.active} onChange={(e) => change("active", e.target.checked)} />
            <span className={`h-6 w-11 rounded-full transition ${form.active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`block h-5 w-5 translate-x-0.5 rounded-full bg-white transition ${form.active ? "translate-x-5" : ""}`} />
            </span>
          </label>
        </div>

      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
